/**
 * Dates render day-first for a Malaysian audience rather than following the
 * viewer's machine, so a deletion date and the inbox timestamp beside it can
 * never disagree about what "3/8" means. Timezone is deliberately NOT pinned:
 * the convention is ours, but the clock belongs to whoever is reading it.
 */
export const LOCALE = 'en-GB';

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]!.toUpperCase())
    .join('');
}

export function formatClock(iso: string): string {
  return new Date(iso).toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit' });
}

export function formatInboxTime(iso: string): string {
  const date = new Date(iso);
  const now = new Date();

  if (date.toDateString() === now.toDateString()) return formatClock(iso);

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';

  return date.toLocaleDateString(LOCALE, { day: '2-digit', month: 'short' });
}

/** A calendar date for profile/detail rows, e.g. "14 Jul 2026". */
export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** When a candidate applied, in calendar days: "Applied today", "Applied 2 days ago", then the date. */
export function formatApplied(iso: string, now: Date = new Date()): string {
  const day = new Date(iso);
  const days = Math.round(
    (new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() -
      new Date(day.getFullYear(), day.getMonth(), day.getDate()).getTime()) /
      86_400_000,
  );
  if (days <= 0) return 'Applied today';
  if (days === 1) return 'Applied yesterday';
  if (days === 2) return 'Applied 2 days ago';
  return `Applied ${formatDate(iso)}`;
}

/**
 * How long ago something was written, for a provenance line under the thing it
 * describes. Coarsens as it ages — past a week the age stops being the useful
 * fact and the date takes over.
 */
export function formatAgo(iso: string, now: number = Date.now()): string {
  const seconds = Math.max(0, Math.round((now - Date.parse(iso)) / 1000));
  if (seconds < 45) return 'just now';
  if (seconds < 3600) return `${Math.max(1, Math.round(seconds / 60))}m ago`;
  if (seconds < 86_400) return `${Math.round(seconds / 3600)}h ago`;
  if (seconds < 604_800) return `${Math.round(seconds / 86_400)}d ago`;
  return formatDate(iso);
}

/**
 * The day a run of messages belongs to, e.g. "Tuesday, 11 Aug".
 *
 * Two calls and a comma rather than one options bag, because `en-GB` renders
 * `{weekday, day, month}` as "Tuesday 11 Aug" — no separator between the name of
 * the day and the date. The comma is what stops the two halves reading as one
 * run of words in a pill barely wider than they are.
 *
 * Weekday in full and month abbreviated: the weekday is the part a reader
 * actually navigates by when scrolling back through a week of chat, and the
 * month is usually the same one they are already in.
 */
export function formatDaySeparator(iso: string): string {
  const date = new Date(iso);
  const weekday = date.toLocaleDateString(LOCALE, { weekday: 'long' });
  return `${weekday}, ${date.toLocaleDateString(LOCALE, { day: 'numeric', month: 'short' })}`;
}

/**
 * Time left before a deletion. The unit tracks urgency: the two retention
 * clocks differ by three orders of magnitude, so a 20-day window reads "20d"
 * while the last hour ticks down in "20:43" — precise exactly when precision
 * starts to matter.
 */
export function formatCountdown(at: number, now: number = Date.now()): string {
  const ms = Math.max(0, at - now);
  const hours = ms / 3_600_000;

  if (hours >= 24) return `${Math.round(hours / 24)}d`;
  if (hours >= 1) return `${Math.floor(hours)}hr`;

  const seconds = Math.floor(ms / 1000);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

/**
 * When a deadline lands, as a phrase to drop into a sentence. A date is
 * meaningless for the 30-minute fade and a clock time is meaningless three
 * weeks out, so the unit follows the distance.
 */
export function formatDeadline(at: number, now: number = Date.now()): string {
  const date = new Date(at);
  if (at - now < 24 * 3_600_000) return `at ${formatClock(date.toISOString())}`;

  return `on ${date.toLocaleDateString(LOCALE, { day: 'numeric', month: 'long', year: 'numeric' })}`;
}

/** Long-form countdown for prose, e.g. "20 days" / "3 hours" / "21 minutes". */
export function formatCountdownLong(at: number, now: number = Date.now()): string {
  const ms = Math.max(0, at - now);
  const hours = ms / 3_600_000;

  if (hours >= 24) return plural(Math.round(hours / 24), 'day');
  if (hours >= 1) return plural(Math.floor(hours), 'hour');

  const minutes = Math.floor(ms / 60_000);
  return minutes < 1 ? 'less than a minute' : plural(minutes, 'minute');
}

/** A monthly amount in Ringgit, the unit said once: "RM 3,500". */
export function formatRinggit(amount: number): string {
  return `RM ${amount.toLocaleString('en-MY')}`;
}

export function plural(count: number, unit: string): string {
  return `${count} ${unit}${count === 1 ? '' : 's'}`;
}

/**
 * How big a saved list is, and how much of it is still reachable.
 *
 * The two numbers come apart on their own: a member that gets ended or swept up
 * by retention leaves the inbox, and a list that says "12" and opens onto nine
 * reads as a bug. So it says both, and only when they differ.
 *
 * Written once because two surfaces say it — the row in the lists index and the
 * subtitle inside the list itself — and they are describing the same list. Two
 * copies is how the index comes to disagree with the page it opens.
 */
export function listSummary(held: number, open: number): string {
  return open === held ? plural(open, 'conversation') : `${open} of ${held} still open`;
}

/** A column head on the calendar: "Mon". */
export function formatWeekday(date: Date): string {
  return date.toLocaleDateString(LOCALE, { weekday: 'short' });
}

/**
 * An hour on the time grid's gutter: "09:00".
 *
 * Through the same clock as every other time in the app, so the gutter can't end
 * up in 12-hour time while the message timestamps beside it are in 24 — and
 * because a column of labels needs one width, which "9:00 AM" and "10:00 AM" are
 * not.
 */
export function formatHourLabel(hour: number): string {
  return formatClock(new Date(2000, 0, 1, hour).toISOString());
}

/**
 * The day an event sits on, for a row that already implies the year:
 * "Tue, 18 Aug".
 *
 * The comma is composed by hand, exactly as `formatDaySeparator` does and for
 * the same reason: en-GB renders {weekday, day, month} as "Tue 18 Aug" with
 * nothing between the two halves, which runs together as one string of words on
 * a card's right-hand edge.
 */
export function formatEventDay(iso: string): string {
  const date = new Date(iso);
  return `${formatWeekday(date)}, ${date.toLocaleDateString(LOCALE, {
    day: 'numeric',
    month: 'short',
  })}`;
}

/** "Tue, 18 August 2026" — the day an event is on, spelled out. */
export function formatEventDate(iso: string): string {
  return `${formatWeekday(new Date(iso))}, ${formatDate(iso)}`;
}

/** "13:00 – 13:30". The en dash is the one the calendar header already uses. */
export function formatTimeRange(startIso: string, endIso: string): string {
  return `${formatClock(startIso)} – ${formatClock(endIso)}`;
}

/**
 * How long an event runs, beside the times that bound it: "30 min",
 * "1 hr 30 min". Short forms, because the sentence around it already said when.
 */
export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest} min`;
  if (rest === 0) return `${hours} hr`;
  return `${hours} hr ${rest} min`;
}

/**
 * How soon, in the words a person would use.
 *
 * The single most useful thing a schedule can say, and the one thing a date
 * cannot: "Tue 18 Aug, 13:00" makes the reader work out where they are relative
 * to it, which is exactly the arithmetic nobody has spare attention for in the
 * middle of a hiring day. "In 2 hours" is the answer to the question they were
 * going to ask.
 */
export function formatRelativeWhen(
  startIso: string,
  endIso: string,
  now: number = Date.now(),
): string {
  const start = Date.parse(startIso);
  const end = Date.parse(endIso);

  if (now >= start && now < end) return 'Happening now';
  if (now >= end) return `${formatCountdownLong(now, end)} ago`;
  return `In ${formatCountdownLong(start, now)}`;
}

/** The month above a date chip's numeral: "AUG". */
export function formatMonthShort(iso: string): string {
  return new Date(iso).toLocaleDateString(LOCALE, { month: 'short' }).toUpperCase();
}

/** A one-letter column head for a compact month grid: "M", "T", "W". */
export function formatWeekdayNarrow(date: Date): string {
  return date.toLocaleDateString(LOCALE, { weekday: 'narrow' });
}

/*
 * The two `<input type="date|time">` serialisers, and the parse back.
 *
 * Hand-written from the local components rather than `toISOString().slice(...)`,
 * which is the classic way to lose a day: that converts to UTC first, so a Chat
 * Day opening at 08:00 in Kuala Lumpur (UTC+8) serialises as the previous date
 * and the date field opens on yesterday.
 */
const pad = (value: number): string => String(value).padStart(2, '0');

export function toDateInput(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function toTimeInput(date: Date): string {
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** The pair of fields back into a moment in the reader's own timezone. */
export function fromDateTimeInput(date: string, time: string): Date {
  const [year, month, day] = date.split('-').map(Number);
  const [hour, minute] = time.split(':').map(Number);
  return new Date(year ?? 0, (month ?? 1) - 1, day ?? 1, hour ?? 0, minute ?? 0);
}

/**
 * A count small enough to sit inside a pill: 847, 1.2k, 34k, 1.5m.
 *
 * The scope pills carry a number that grows with the employer, and a large one
 * is what breaks them — "1,200" is four glyphs and a comma in a control sized
 * for a word, and it pushes the label it belongs to off the row. So the count
 * changes unit rather than the pill changing width.
 *
 * **Truncated, never rounded.** 1,999 reads as "1.9k", not "2k". Rounding up is
 * how a number that is nearly two thousand comes to claim it is exactly two
 * thousand, and this is a count of work waiting — an employer reading it is
 * deciding whether to start. Under-reporting an approximation is the safe
 * direction; the exact figure is a hover away (see the pill's `title`).
 *
 * Hand-rolled rather than `Intl` with `notation: 'compact'`, for two reasons:
 * that one rounds, and it localises the unit, so the same inbox would read
 * "1.2K" on one machine and "1,2 k" on another. The dates in this file pin their
 * locale for exactly that reason; a suffix on a badge does not need one at all.
 */
export function compactCount(count: number): string {
  if (count < 1000) return String(count);
  const [divisor, suffix] = count < 1_000_000 ? [1000, 'k'] : [1_000_000, 'm'];
  // One decimal, floored — see above. `Math.floor` on the tenth rather than
  // `toFixed`, which rounds.
  const scaled = Math.floor((count / divisor) * 10) / 10;
  // A whole number drops the ".0": "1k" reads as a number, "1.0k" reads as a
  // measurement taken to one decimal place.
  return `${Number.isInteger(scaled) ? scaled : scaled.toFixed(1)}${suffix}`;
}
